import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { useTranslation } from "react-i18next";

export interface SearchableOption {
  id: string;
  displayString: string;
}

interface SearchableComboboxProps {
  placeholder?: string;
  searchPlaceholder?: string;
  emptyResults?: string;
  value: SearchableOption | null;
  onSelect: (value: SearchableOption | null) => void;
  onSearchChange: (search: string) => void;
  options: SearchableOption[];
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  disabled?: boolean;
}

export function SearchableCombobox({
  placeholder,
  searchPlaceholder,
  emptyResults,
  value,
  onSelect,
  onSearchChange,
  options,
  loading = false,
  hasMore = false,
  onLoadMore,
  disabled = false,
}: SearchableComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const observerTarget = useRef<HTMLDivElement>(null);

  const finalPlaceholder =
    placeholder || t("searchableCombobox.defaultPlaceholder");
  const finalSearchPlaceholder =
    searchPlaceholder || t("searchableCombobox.defaultSearchPlaceholder");
  const finalEmptyResults =
    emptyResults || t("searchableCombobox.defaultEmptyResults");

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, onSearchChange]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && !loading && hasMore && onLoadMore) {
        onLoadMore();
      }
    },
    [loading, hasMore, onLoadMore],
  );

  useEffect(() => {
    if (!open || !hasMore || !onLoadMore) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [open, hasMore, onLoadMore, handleIntersection]);

  const handleSelect = useCallback(
    (option: SearchableOption) => {
      onSelect(option);
      setOpen(false);
    },
    [onSelect],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger disabled={disabled} asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 w-full justify-between border-border-dark text-muted-foreground"
          disabled={disabled}
        >
          <span
            className={cn(value ? "text-foreground" : "text-muted-foreground")}
          >
            {value?.displayString || finalPlaceholder}
          </span>
          <ChevronsUpDown strokeWidth="3" className="text-input-icon" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        style={{ width: "var(--radix-popover-trigger-width)" }}
        className="p-0"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={finalSearchPlaceholder}
            value={search}
            onValueChange={setSearch}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>
              {loading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : search ? (
                finalEmptyResults
              ) : (
                ""
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => handleSelect(option)}
                >
                  {option.displayString}
                  <Check
                    className={cn(
                      "ml-auto",
                      value?.id === option.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
              {hasMore && <div ref={observerTarget} className="h-4 w-full" />}
              {loading && hasMore && (
                <div className="flex justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
